/**
 * How the web app authenticates and paces its WebSockets. Pure — no React, no
 * axios — so it is unit-tested (wsPolicy.test.js).
 *
 * A JavaScript port of the customer and rider apps' `wsPolicy.ts`; the rules
 * are the same so every Yum Express client treats the ticket endpoint alike.
 *
 * Flow for every socket: POST /api/auth/ws-ticket (Bearer, via the HTTP
 * client) → `{ ticket, expires_in }` → connect to `<path>?ticket=<ticket>`.
 * A ticket is single-use and short-lived, so each connect needs a fresh one
 * and two sockets can never share one. There is deliberately NO fallback to
 * `?token=<JWT>`: nginx logs query strings, and falling back on every 429 is
 * what turned the ticket throttle into a reconnect storm.
 *
 * TicketResult:
 *   { kind: 'ok', ticket }
 *   { kind: 'rate_limited', retryAfterMs }  429 — wait at least retryAfterMs (null = no hint)
 *   { kind: 'auth' }                        401/403 after the HTTP client's own refresh failed
 *   { kind: 'transient' }                   network error, timeout, 5xx: retry with backoff
 */

/** Longest Retry-After honoured, so a bad header cannot park a socket for hours. */
export const MAX_RETRY_AFTER_MS = 10 * 60_000

/**
 * Retry-After as milliseconds from `nowMs`: either delta-seconds ("30") or an
 * HTTP date. Null when absent or unparseable.
 */
export function parseRetryAfter(value, nowMs) {
  if (value == null) return null
  const raw = String(Array.isArray(value) ? value[0] : value).trim()
  if (!raw) return null
  let ms
  if (/^\d+(\.\d+)?$/.test(raw)) {
    ms = Number(raw) * 1000
  } else {
    const at = Date.parse(raw)
    if (Number.isNaN(at)) return null
    ms = at - nowMs
  }
  if (!Number.isFinite(ms)) return null
  return Math.min(MAX_RETRY_AFTER_MS, Math.max(0, Math.round(ms)))
}

/** Map a failed ticket request (HTTP status + headers) to what to do next. */
export function classifyTicketFailure(status, headers, nowMs) {
  if (status === 429) {
    const h = headers || {}
    return { kind: 'rate_limited', retryAfterMs: parseRetryAfter(h['retry-after'] ?? h['Retry-After'], nowMs) }
  }
  if (status === 401 || status === 403) return { kind: 'auth' }
  return { kind: 'transient' }
}

/**
 * Exponential backoff with "equal jitter": half the exponential step is fixed,
 * half random. Never zero (no tight loop), never above `capMs`, and sockets
 * that dropped together do not reconnect together.
 */
export function backoffDelay(attempt, random, { baseMs = 1_000, capMs = 30_000 } = {}) {
  const exp = Math.min(capMs, baseMs * 2 ** Math.max(0, attempt))
  const r = Math.min(1, Math.max(0, random()))
  return Math.round(exp / 2 + (exp / 2) * r)
}

/** The only way this app builds an authenticated WebSocket URL. */
export function ticketUrl(wsBase, path, ticket) {
  return `${wsBase}${path}?ticket=${encodeURIComponent(ticket)}`
}

/**
 * A pause every socket in the app respects. One socket getting a 429 means
 * the account's ticket budget is spent, so the others must not keep asking.
 */
export class Cooldown {
  constructor() {
    this.until = 0
  }

  hold(untilMs) {
    if (untilMs > this.until) this.until = untilMs
  }

  remaining(nowMs) {
    return Math.max(0, this.until - nowMs)
  }

  reset() {
    this.until = 0
  }
}

/** Shared by every socket in the running page. */
export const ticketCooldown = new Cooldown()
